import React, { createContext, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { ProposedChangeWorksheetRow } from 'components/Collaboration/utils/collaboration-types';
import { PlanScenarioApprovalParams } from 'constants/routes';
import { useIsFeatureEnabled } from 'contexts/FeatureFlags';
import { TQ_ACTIONS } from 'contexts/Permissions/constants';
import { usePermissions } from 'contexts/Permissions/Permissions';
import { UsersService } from 'services';
import { ProposedChangeGroupsServiceV2 } from 'services/ProposedChangeGroupsServiceV2';
import { SPMApprovalsService } from 'services/spmApprovals';
import type { StringifiedUUID } from 'types';
import { ProposedChangeGroupStatus } from 'types/SPM';
import type { SPMApproval } from 'types/SPM';

type ApprovalContextValue = {
  approvalId: string | undefined;
  approval: SPMApproval | undefined;
  isLoading: boolean;
  isApprovalMode: boolean;
  isOverrideMode: boolean;
  canProposeChanges: boolean;
  isApprovalWorkflowsEnabled: boolean;
  proposedChangeGroupId: StringifiedUUID | undefined;
  activeChangeGroupId: StringifiedUUID | undefined;
  showProposedChanges: boolean;
  setShowProposedChanges: (show: boolean) => void;
};

const defaultContextValue: ApprovalContextValue = {
  approvalId: undefined,
  approval: undefined,
  isLoading: false,
  isApprovalMode: false,
  isOverrideMode: false,
  canProposeChanges: true,
  isApprovalWorkflowsEnabled: false,
  proposedChangeGroupId: undefined,
  activeChangeGroupId: undefined,
  showProposedChanges: false,
  setShowProposedChanges: () => {},
};

const ApprovalContext = createContext<ApprovalContextValue>(defaultContextValue);

export const useApprovalContext = () => {
  return useContext(ApprovalContext);
};

/**
 * Hook to fetch proposed change rows for a specific worksheet.
 * Uses ApprovalContext for the change group ID and toggle state.
 * Calls the TQ proposed-change-rows endpoint which handles UUID→composite key translation.
 */
export const useProposedChangeRows = (
  worksheetId: StringifiedUUID | undefined,
): ProposedChangeWorksheetRow[] | undefined => {
  const { proposedChangeGroupId, showProposedChanges, isApprovalMode } = useApprovalContext();

  const rowsQuery = ProposedChangeGroupsServiceV2.useSPMProposedChangeRowsQuery({
    changeGroupId: proposedChangeGroupId,
    options: { enabled: isApprovalMode && showProposedChanges && !!proposedChangeGroupId },
  });

  return useMemo(() => {
    if (!showProposedChanges) return undefined;
    if (!worksheetId) return undefined;
    return rowsQuery.data?.data?.filter((row) => row.worksheet_id === worksheetId);
  }, [rowsQuery.data?.data, worksheetId, showProposedChanges]);
};

/**
 * Hook to get the total count of proposed change rows across all worksheets.
 * Not gated by showProposedChanges so the count is always available in the status bar.
 */
export const useProposedChangeCount = (): number | undefined => {
  const { proposedChangeGroupId, isApprovalMode } = useApprovalContext();

  const rowsQuery = ProposedChangeGroupsServiceV2.useSPMProposedChangeRowsQuery({
    changeGroupId: proposedChangeGroupId,
    options: { enabled: isApprovalMode && !!proposedChangeGroupId },
  });

  return rowsQuery.data?.total_count;
};

/**
 * Context provider for the approval flow.
 *
 * Reads approvalId from URL route params and fetches approval details.
 * Provides the proposedChangeGroupId and toggle state for downstream use.
 */
export const ApprovalContextProvider: React.FC = ({ children }) => {
  const { approvalId } = useParams<Partial<PlanScenarioApprovalParams>>();
  const isApprovalWorkflowsEnabled = useIsFeatureEnabled('feature_flag_spm_approval_workflows');
  const isApprovalMode = isApprovalWorkflowsEnabled && !!approvalId;

  const [showProposedChanges, setShowProposedChanges] = useState(true);

  // Fetch approval data when approvalId is present and feature flag is enabled
  const approvalQuery = SPMApprovalsService.useGetApprovalQuery(approvalId, {
    enabled: isApprovalMode,
  });

  const approval = approvalQuery.data;
  const proposedChangeGroupId = approval?.approval_group?.object_id;
  const activeChangeGroupId = showProposedChanges ? proposedChangeGroupId : undefined;

  // Determine override mode: user has TQ admin access and is in approval mode,
  // but is NOT the assigned approver — so they can only act via override.
  const { hasAccess } = usePermissions();
  const hasRbacAccess = hasAccess({ actions: TQ_ACTIONS.TQ_READ });
  const { query: userQuery } = UsersService.useGetCurrentUserQuery();
  const currentUser = userQuery.data;
  const isOverrideMode = hasRbacAccess && isApprovalMode && !!approval && approval.approver_id !== currentUser?.id;

  // Whether the approval state allows proposing changes.
  // Consumers should gate on isApprovalMode before consulting this.
  // Group-level resolution takes precedence over individual approval resolution.
  // Always false when the proposed change group has been closed (changes already applied).
  const canProposeChanges = (() => {
    if (approval?.approval_group?.proposed_change_group_status === ProposedChangeGroupStatus.CLOSED) return false;
    if (isOverrideMode) return true;

    const resolvedStatus = approval?.approval_group?.is_approved ?? approval?.is_approved;
    return resolvedStatus === null || resolvedStatus === undefined;
  })();

  const value = useMemo((): ApprovalContextValue => {
    return {
      approvalId,
      approval,
      isLoading: approvalQuery.isLoading,
      isApprovalMode,
      isOverrideMode,
      canProposeChanges,
      isApprovalWorkflowsEnabled,
      proposedChangeGroupId,
      activeChangeGroupId,
      showProposedChanges,
      setShowProposedChanges,
    };
  }, [
    approvalId,
    approval,
    approvalQuery.isLoading,
    isApprovalMode,
    isOverrideMode,
    canProposeChanges,
    isApprovalWorkflowsEnabled,
    proposedChangeGroupId,
    activeChangeGroupId,
    showProposedChanges,
  ]);

  return <ApprovalContext.Provider value={value}>{children}</ApprovalContext.Provider>;
};
